import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Icon,
  Paper,
  Card,
} from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';

// Helper function to filter an object's keys based on an allowed list
const filterObjectByKeys = (raw, allowed) => {
  if (!raw || !allowed || !Array.isArray(allowed)) return {};
  return Object.keys(raw)
    .filter((key) => allowed.includes(key))
    .reduce((obj, key) => {
      obj[key] = raw[key];
      return obj;
    }, {});
};

function RateCardsTab({ product, renderFieldsProp, basicFields }) {
  const [expandedSchedule, setExpandedSchedule] = useState({});

  // Get the array of consumption schedules
  const consumptionSchedules = product?.related_data?.consumptionSchedules || [];

  // Check if any schedules exist. If not, show the "not available" message.
  if (!consumptionSchedules || consumptionSchedules.length === 0) {
    return (
      <ArgonBox p={3}>
        <ArgonTypography variant="body2">No Rate Cards available for this product.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleScheduleClick = (scheduleId) => {
    setExpandedSchedule((prevState) => ({
      ...prevState,
      [scheduleId]: !prevState[scheduleId],
    }));
  };

  return (
    <ArgonBox p={3}>
      {consumptionSchedules.map((scheduleData) => {
        // Handle both wrapped and unwrapped structures
        const schedule = scheduleData.consumptionSchedules || scheduleData;
        const rates = scheduleData.consumptionRates || [];
        const scheduleId = schedule.sfId || schedule.id;
        const hasRates = rates && rates.length > 0;

        // Filter the schedule's main fields based on the visibility config
        const fieldsToDisplay = filterObjectByKeys(schedule.fields, basicFields);

        return (
          <Card key={scheduleId} sx={{ marginBottom: '1rem' }}>
            <ArgonBox mb={0.5} mt={0.5}>
              {/* --- Clickable Header for each Schedule --- */}
              <ArgonBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => handleScheduleClick(scheduleId)}
                style={{ cursor: 'pointer' }}
                p={1}
                borderRadius="md"
              >
                <ArgonTypography variant="h6" fontWeight="medium" color="secondary" ml={1}>
                  {schedule.name || 'Unnamed Schedule'}
                </ArgonTypography>
                <IconButton
                  aria-label="expand schedule"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent parent onClick from firing again
                    handleScheduleClick(scheduleId);
                  }}
                >
                  <Icon>{expandedSchedule[scheduleId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              {/* --- Collapsible Content for the Schedule --- */}
              <Collapse in={expandedSchedule[scheduleId]} timeout="auto" unmountOnExit>
                <ArgonBox p={2}>
                  {/* Render general schedule fields */}
                  {renderFieldsProp(fieldsToDisplay)}

                  {/* --- Table for Consumption Rates --- */}
                  {hasRates && (
                    <ArgonBox mt={3}>
                      <ArgonTypography variant="subtitle2" fontWeight="bold" mb={1}>
                        Consumption Rates
                      </ArgonTypography>
                      <TableContainer component={Paper} elevation={0} variant="outlined">
                        <Table size="small" aria-label="consumption rates">
                          <TableHead sx={{ display: 'contents' }}>
                            <TableRow>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Lower Bound
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Upper Bound
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Price
                                </ArgonTypography>
                              </TableCell>
                              <TableCell>
                                <ArgonTypography variant="caption" fontWeight="bold">
                                  Pricing Method
                                </ArgonTypography>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rates
                              .sort((a, b) => {
                                const orderA = a.fields?.ProcessingOrder || 0;
                                const orderB = b.fields?.ProcessingOrder || 0;
                                return orderA - orderB;
                              })
                              .map((rate) => (
                                <TableRow key={rate.sfId || rate.id}>
                                  <TableCell>{rate.fields?.LowerBound ?? 'N/A'}</TableCell>
                                  <TableCell>{rate.fields?.UpperBound ?? '∞'}</TableCell>
                                  <TableCell>{rate.fields?.Price ?? 'N/A'}</TableCell>
                                  <TableCell>{rate.fields?.PricingMethod ?? 'N/A'}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </ArgonBox>
                  )}
                </ArgonBox>
              </Collapse>
            </ArgonBox>
          </Card>
        );
      })}
    </ArgonBox>
  );
}

export default RateCardsTab;
